import { AppMessageService } from '@/core/services/app-message.service';
import { RestService } from '@/core/services/rest.service';
import { AppConstants } from '@/core/utils/app.const';
import { AppStorage } from '@/core/utils/app.storage';
import { AppStorageService } from '@/core/utils/app.storage.service';
import { LayoutServiceCustom as LayoutService } from '@/layout/service/layout.custom';
import { ResponseDto } from '@/models/app.response-dto';
import { P360DropdownItem } from '@/models/common';
import { Company } from '@/models/company';
import { Gen0001 } from '@/models/general-information/gen-0001.customer';
import { Gen0003 } from '@/models/general-information/gen-0003.customer-complete';
import { SaveRoute } from '@/models/save/save-route';
import { SecurityUser } from '@/models/security/security-user';
import { appStoreFiltersActions } from '@/store/filters/store.filters.action';
import { appStoreGlobalActions } from '@/store/global/store.global.action';
import { appStoreGlobalIS } from '@/store/global/store.global.state';
import { appStoreScreenActions } from '@/store/screen/store.screen.action';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Camera, CameraResultType } from '@capacitor/camera';
import { PermissionState } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Store } from '@ngrx/store';
import { from, Observable, of, throwError } from 'rxjs';
import { finalize, map, mergeMap, tap, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseService {
  private timeout = 20000;

  protected readonly rest = inject(RestService);
  protected readonly messageService = inject(AppMessageService);
  protected readonly storage = inject(AppStorageService);
  protected readonly http = inject(HttpClient);
  protected readonly store = inject(Store<any>);
  protected readonly layoutService = inject(LayoutService);

  handleRestErrorMessage = (error: any, showWarning = false, sticky = false) => {
    const isString = typeof error == 'string';
    const message = isString ? error : error.message;
    console.error('ERROR! ', error);

    const messageService =
      message.indexOf('MESSAGES.NO-CONTENT') > -1 || showWarning
        ? this.messageService?.addWarning
        : this.messageService?.addError;
    messageService(message, {}, sticky);
  };

  cleanRestCache = () => {
    this.storage.get(AppStorage.CACHE_KEYS).then(async (value: string) => {
      const keys = (value ?? '').split('||');
      const removed = [];
      for (const key of keys) {
        await this.storage.remove(key);
        removed.push(key);
      }
      this.storage.set(AppStorage.CACHE_KEYS, removed.join('||'));
    });
  };

  requestGpsPermissions = (method: () => void) => {
    Geolocation.checkPermissions().then((status) => {
      if (status.location !== 'granted' || status.coarseLocation !== 'granted') {
        Geolocation.requestPermissions({
          permissions: ['location', 'coarseLocation'],
        })
          .then(
            () => {},
            () => this.requestGpsPermissions(method),
          )
          .catch(() => this.requestGpsPermissions(method));
      } else {
        method();
      }
    });
  };

  requestCameraPermissions = (method: () => void) => {
    Camera.checkPermissions().then((status) => {
      if (status.camera !== 'granted' || status.photos !== 'granted') {
        Camera.requestPermissions({
          permissions: ['camera', 'photos'],
        })
          .then(
            () => {},
            () => this.requestCameraPermissions(method),
          )
          .catch(() => this.requestCameraPermissions(method));
      } else {
        method();
      }
    });
  };

  getPhoto = async (
    identifier: string | number,
  ): Promise<{
    photo: Blob;
    photoUri: string;
    photoPath: string;
    photoName: string;
  }> => {
    const picture = await Camera.getPhoto({
      resultType: CameraResultType.Base64,
      saveToGallery: true,
      height: 800,
      width: 480,
      correctOrientation: true,
    });

    if (!picture.base64String) {
      return Promise.reject();
    }

    const imageData = picture.base64String;
    const photoUri = imageData;
    const photoPath = `data:image/jpg;base64,${imageData}`;
    const byteChars = atob(imageData);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const photo = new Blob([byteArray], { type: 'image/jpg' });
    const photoName = `${new Date().getTime()}_${identifier}_img`;

    return { photo, photoUri, photoPath, photoName };
  };

  failOnGetFile = (error: any) => {
    console.error('File Error! ', error);
  };

  getReverseAddress = ({
    lat,
    lon,
  }: {
    lat: number;
    lon: number;
  }): Observable<{
    address?: string;
    countryCode?: string;
    countryName?: string;
    postalCode?: string;
    state?: string;
    county?: string;
    town?: string;
  }> => {
    return this.http
      ?.get('https://nominatim.openstreetmap.org/reverse.php', {
        params: {
          lat,
          lon,
          zoom: 18,
          format: 'jsonv2',
        },
      })
      .pipe(
        map((result: any) => ({
          address: result['display_name'],
          countryCode: result['address']['country_code'],
          countryName: result['address']['country'],
          postalCode: result['address']['postcode'],
          state: result['address']['state'],
          county: result['address']['county'],
          town: result['address']['town'],
        })),
      );
  };

  getGen0003 = (params: any): Observable<ResponseDto<Gen0003>> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0003, params);

  whoAmI = () => {
    return this.rest
      .get<SecurityUser>({
        url: `${AppConstants.SECURITY_HOST}/who-am-i`,
      })
      .pipe(
        tap({
          next: (res) => {
            if (!res.collaborator) {
              this.storage.remove(AppStorage.SESSION).then(() => {
                window.location.reload();
              });
              return;
            }

            if (!res.company.theme) {
              res.company.theme = {
                baseColor: 'p360',
                planChart: appStoreGlobalIS.appColors.charts.plan,
                planText: appStoreGlobalIS.appColors.text.plan,
                currentValueChart: appStoreGlobalIS.appColors.charts.sales,
                currentValueChartAlt: appStoreGlobalIS.appColors.charts.salesAlt,
                currentValueText: appStoreGlobalIS.appColors.text.sales,
                currentValueTextAlt: appStoreGlobalIS.appColors.text.salesAlt,
                previousValueChart: appStoreGlobalIS.appColors.charts.salesPrev,
                previousValueText: appStoreGlobalIS.appColors.text.salesPrev,
                fulfillmentChart: appStoreGlobalIS.appColors.charts.fulfillment,
                fulfillmentText: appStoreGlobalIS.appColors.text.fulfillment,
                previousFulfillmentChart: appStoreGlobalIS.appColors.charts.fulfillmentPrev,
                previousFulfillmentText: appStoreGlobalIS.appColors.text.fulfillmentPrev,
                evolutionChart: appStoreGlobalIS.appColors.charts.projection,
                evolutionText: appStoreGlobalIS.appColors.text.projection,
              };
            }

            this.storage.set(AppStorage.USER, res);
            this.store.dispatch(appStoreGlobalActions.setUserAction({ user: res }));
            this.storage.set(AppStorage.USERNAME, res.username);
            this.storage.set(AppStorage.COLLABORATOR, res.collaborator);
            this.store.dispatch(
              appStoreGlobalActions.setCollaboratorAction({
                collaborator: res.collaborator,
              }),
            );

            this.storage.set(AppStorage.COMPANY, res.company);
            this.layoutService.updateColors('primary', res.company.theme.baseColor);
            this.storage.set(AppStorage.COMPANY_TYPE, res.company.extra?.toLowerCase());

            this.store.dispatch(
              appStoreGlobalActions.setNotificationCounterAction({
                notificationCounter: res.notifCounter!,
              }),
            );

            this.store.dispatch(
              appStoreScreenActions.setClaimsAction({
                claims: res.claims!,
              }),
            );
          },
        }),
      );
  };

  saveRoute = (route: SaveRoute) => {
    if (!route.address) {
      route.address = '';
    }
    return this.restPost(AppConstants.REST_ENDPOINTS.SAVE_ROUTE, route);
  };

  loadClients = (document: string, isRefresh = false, showToast = true, actionAfter = () => {}) => {
    this.storage.get<P360DropdownItem<Gen0001>[]>(AppStorage.CLIENTS).then((clients) => {
      if (isRefresh || clients == null) {
        this.getGen0001({ document }, showToast)
          .pipe(
            finalize(() => {
              this.handleLoadInfoCounter();
            }),
          )
          .subscribe({
            next: (data) => {
              const clients =
                data.results?.map((item: any) => ({
                  ...item,
                  isNew: false,
                })) ?? [];

              const clientFilterList: P360DropdownItem<Gen0001>[] = [
                {
                  id: 0,
                  label: 'ALL',
                  value: {
                    id: 0,
                    name: 'ALL',
                    code: 'TODOS',
                  },
                },
                ...clients.map((item, idx) => ({
                  id: idx + 1,
                  label: item.name,
                  value: { id: idx + 1, ...item },
                })),
              ];

              this.storage.remove(AppStorage.CLIENTS);
              if (clients.length > 0) {
                this.storage.set(AppStorage.CLIENTS, clientFilterList);

                this.store.dispatch(
                  appStoreFiltersActions.setClientListAction({
                    clientFilterList,
                  }),
                );
              }

              actionAfter();
            },
            error: () => {
              actionAfter();
            },
          });
      } else {
        this.handleLoadInfoCounter();
        this.store.dispatch(
          appStoreFiltersActions.setClientListAction({
            clientFilterList: clients,
          }),
        );
        actionAfter();
      }
    });
  };

  handleCheckPositionPermission = async (): Promise<PermissionState> => {
    try {
      const permission = await Geolocation.checkPermissions();
      return permission.location;
    } catch (error) {
      this.messageService.error({ message: 'Necesitar permitir el acceso al GPS de tu dispositivo.', sticky: true });
      await Geolocation.requestPermissions({ permissions: ['coarseLocation', 'location'] });
      return 'denied';
    }
  };

  protected restGet = <T = any>(
    endpoint: string,
    pathParams: any,
    urlParams?: any,
    triggerLoading: boolean = false,
    overrideTimeout?: number,
  ): Observable<T> => {
    if (!this.rest || !this.storage) {
      return throwError(() => new Error('You need to pass rest & storage service'));
    }

    this.handleTriggerLoading(triggerLoading);
    return from(this.storage.get<Company>(AppStorage.COMPANY)).pipe(
      mergeMap((storage) => {
        const hostUrl = storage?.hostUrl ?? '';
        const companyUri = `${hostUrl}/${AppConstants.MOBILE_HOST}`;
        const urlWithParams = this.rest.addParamsToPath(`${companyUri}/${endpoint}`, pathParams, urlParams);

        return from(this.storage.get(urlWithParams)).pipe(
          mergeMap((resultInCache) => {
            let finalObservable = this.rest
              .get({
                url: `${companyUri}/${AppConstants.REST_ENDPOINTS.HEALTHCHECK}`,
              })
              .pipe(
                mergeMap((res) => {
                  if (res) {
                    const apiCall = this.rest.get({ url: urlWithParams });
                    if (overrideTimeout === -1) {
                      return apiCall;
                    }
                    return apiCall.pipe(timeout(overrideTimeout ?? this.timeout));
                  }
                  return throwError(() => new Error('MESSAGE.500_GENERIC'));
                }),
              );
            if (!this.isEndpointIgnored(endpoint) && resultInCache) {
              finalObservable = of(resultInCache);
            }
            return finalObservable.pipe(finalize(this.finalizeLoading));
          }),
        );
      }),
      finalize(this.finalizeLoading),
    );
  };

  protected restPost = <T = any>(
    endpoint: string,
    body: any,
    queryParams?: any,
    urlParams?: any,
    triggerLoading: boolean = false,
    triggerFinalize: boolean = false,
  ): Observable<ResponseDto<T>> => {
    if (!this.rest || !this.storage) {
      return throwError(() => new Error('You need to pass rest & storage service'));
    }

    this.handleTriggerLoading(triggerLoading);
    return from(this.storage.get<Company>(AppStorage.COMPANY)).pipe(
      mergeMap((storage) => {
        const hostUrl = storage?.hostUrl ?? '';
        const companyUri = `${hostUrl}/${AppConstants.MOBILE_HOST}`;

        return this.rest.get({ url: `${companyUri}/${AppConstants.REST_ENDPOINTS.HEALTHCHECK}` }).pipe(
          mergeMap((res) => {
            if (res) {
              return this.rest.post({
                url: `${companyUri}/${endpoint}`,
                body,
                urlParams,
                queryParams,
              });
            }
            return throwError(() => new Error('MESSAGE.500_GENERIC'));
          }),
        );
      }),
      finalize(() => this.finalizeLoading(triggerFinalize)),
    );
  };

  protected restPostFile = <T = any>(
    endpoint: string,
    data: FormData,
    urlParams?: any,
    queryParams?: any,
    triggerLoading: boolean = false,
  ): Observable<ResponseDto<T>> => {
    if (!this.rest || !this.storage) {
      return throwError(() => new Error('You need to pass rest & storage service'));
    }

    this.handleTriggerLoading(triggerLoading);
    return from(this.storage.get<Company>(AppStorage.COMPANY)).pipe(
      mergeMap((storage) => {
        const hostUrl = storage?.hostUrl ?? '';
        const companyUri = `${hostUrl}/${AppConstants.MOBILE_HOST}`;

        return this.rest
          .get({
            url: `${companyUri}/${AppConstants.REST_ENDPOINTS.HEALTHCHECK}`,
          })
          .pipe(
            mergeMap((res) => {
              if (res) {
                return this.rest.uploadFile(`${companyUri}/${endpoint}`, data, urlParams, queryParams);
              }
              return throwError(() => new Error('MESSAGE.500_GENERIC'));
            }),
          );
      }),
      finalize(this.finalizeLoading),
    );
  };

  protected restPatch = <T = any>(
    endpoint: string,
    body?: any,
    urlParams?: any,
    pathParams?: any,
    triggerLoading: boolean = false,
  ): Observable<ResponseDto<T>> => {
    if (!this.rest || !this.storage) {
      return throwError(() => new Error('You need to pass rest & storage service'));
    }

    this.handleTriggerLoading(triggerLoading);
    return from(this.storage.get<Company>(AppStorage.COMPANY)).pipe(
      mergeMap((storage) => {
        const hostUrl = storage?.hostUrl ?? '';
        const companyUri = `${hostUrl}/${AppConstants.MOBILE_HOST}`;

        return this.rest.get({ url: `${companyUri}/${AppConstants.REST_ENDPOINTS.HEALTHCHECK}` }).pipe(
          mergeMap((res) => {
            if (res) {
              return this.rest.patch({ url: `${companyUri}/${endpoint}`, body, urlParams, queryParams: pathParams });
            }
            return throwError(() => new Error('MESSAGE.500_GENERIC'));
          }),
        );
      }),
      finalize(this.finalizeLoading),
    );
  };

  protected restPut = <T = any>(
    endpoint: string,
    body?: any,
    urlParams?: any,
    pathParams?: any,
    triggerLoading: boolean = false,
  ): Observable<ResponseDto<T>> => {
    if (!this.rest || !this.storage) {
      return throwError(() => new Error('You need to pass rest & storage service'));
    }

    this.handleTriggerLoading(triggerLoading);
    return from(this.storage.get<Company>(AppStorage.COMPANY)).pipe(
      mergeMap((storage) => {
        const hostUrl = storage?.hostUrl ?? '';
        const companyUri = `${hostUrl}/${AppConstants.MOBILE_HOST}`;

        return this.rest.get({ url: `${companyUri}/${AppConstants.REST_ENDPOINTS.HEALTHCHECK}` }).pipe(
          mergeMap((res) => {
            if (res) {
              return this.rest.put({ url: `${companyUri}/${endpoint}`, body, urlParams, queryParams: pathParams });
            }
            return throwError(() => new Error('MESSAGE.500_GENERIC'));
          }),
        );
      }),
      finalize(this.finalizeLoading),
    );
  };

  protected restDelete = <T = any>(
    endpoint: string,
    urlParams: any,
    params?: any,
    triggerLoading: boolean = false,
  ): Observable<T> => {
    if (!this.storage)
      if (!this.rest || !this.storage) {
        return throwError(() => new Error('You need to pass rest & storage service'));
      }

    this.handleTriggerLoading(triggerLoading);
    return from(this.storage.get<Company>(AppStorage.COMPANY)).pipe(
      mergeMap((storage) => {
        const hostUrl = storage?.hostUrl ?? '';
        const companyUri = `${hostUrl}/${AppConstants.MOBILE_HOST}`;

        return this.rest
          .get({
            url: `${companyUri}/${AppConstants.REST_ENDPOINTS.HEALTHCHECK}`,
          })
          .pipe(
            timeout(this.timeout),
            mergeMap((res) => {
              if (res) {
                return this.rest
                  .delete({
                    url: `${companyUri}/${endpoint}`,
                    urlParams,
                    queryParams: params,
                  })
                  .pipe(timeout(this.timeout));
              }
              return throwError(() => new Error('MESSAGE.500_GENERIC'));
            }),
          );
      }),
      finalize(this.finalizeLoading),
    );
  };

  protected handleLoadInfoCounter = () => {};

  private isEndpointIgnored = (endpoint: string) =>
    endpoint === AppConstants.REST_ENDPOINTS.HEALTHCHECK ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0000 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0001 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0002 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0003 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0019 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0021 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0022 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0030 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0033 || // TODO: Not needed anymore
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0036 ||
    endpoint === AppConstants.REST_ENDPOINTS.SELLIN_0008 ||
    endpoint === AppConstants.REST_ENDPOINTS.SELLIN_0028 ||
    endpoint === AppConstants.REST_ENDPOINTS.RUT_0001 ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0040 ||
    endpoint === AppConstants.REST_ENDPOINTS.CAMPAIGN_RESOURCE ||
    endpoint === AppConstants.REST_ENDPOINTS.GEN_0041;

  private handleTriggerLoading = (triggerLoading: boolean = false) => {
    if (triggerLoading) {
      this.store.dispatch(
        appStoreGlobalActions.setIsLoadingAction({
          isLoading: true,
        }),
      );
    }
  };

  private finalizeLoading = (triggerLoading: boolean = true) => {
    if (triggerLoading) {
      setTimeout(() => {
        this.store.dispatch(
          appStoreGlobalActions.setIsLoadingAction({
            isLoading: false,
          }),
        );
      }, 0);
    }
  };

  private readonly getGen0001 = (params: any, showToast = true): Observable<{ results?: Gen0001[] }> =>
    this.restGet(AppConstants.REST_ENDPOINTS.GEN_0001, params).pipe(
      tap({
        next: () => {
          if (showToast) {
            this.messageService.addSuccess('MESSAGE.200_PREFIX#MESSAGE.200_SYNC_GEN_0001', {}, true);
          }
        },
      }),
    );
}
